/**
 * Politique de confidentialité — FR (fait foi).
 * Texte transmis par le client, publié verbatim.
 * Corrections apportées (listées dans le rapport final Phase 11) :
 *   - Section 4.4 : ajout du paragraphe de tête sur Stripe / paiement site web
 *   - Section 13 : ajout du paragraphe "Pour les comptes créés sur openmeta.tech..."
 *   - Section 14 : "l'évolution de MedLib" → "l'évolution d'OpenMeta"
 */
export const privacyFr = {
  eyebrow: 'Documents légaux',
  title: 'Politique de confidentialité',
  subtitle:
    "OPENMETA respecte votre vie privée et applique les principes du Règlement général sur la protection des données (RGPD) ainsi que la loi Informatique et Libertés. La présente politique décrit les données que nous traitons, les finalités poursuivies, les destinataires, les durées de conservation, ainsi que vos droits.",
  updated: '8 septembre 2026',
  seoTitle: 'Politique de confidentialité — OpenMeta',
  seoDescription:
    "Politique de confidentialité d'OpenMeta : données traitées, finalités, bases juridiques, durées de conservation, transferts, sécurité, droits des utilisateurs.",
  sections: [
    {
      id: 'responsable',
      title: 'Responsable du traitement',
      blocks: [
        {
          type: 'p',
          content:
            "Le responsable du traitement des données personnelles est OPENMETA, société par actions simplifiée au capital de 40 000 €, immatriculée au Registre du commerce et des sociétés d'Alençon sous le numéro 978 390 813, dont le siège social est situé 5037 Chemin de la Veillotière, 61330 Ceaucé, France.",
        },
        {
          type: 'p',
          content:
            "Numéro d'identification européen : FR6101.978390813.",
        },
        {
          type: 'p',
          content:
            "Pour toute demande relative à vos données personnelles, vous pouvez contacter OPENMETA à l'adresse : support@openmeta.fr.",
        },
      ],
    },
    {
      id: 'objet',
      title: "Objet de l'application OpenMeta",
      blocks: [
        {
          type: 'p',
          content:
            "L'application OpenMeta fournit au grand public des informations générales de santé, un parcours d'orientation, des fiches de premiers secours, un accès aux numéros utiles et, selon les fonctions activées, une recherche de soins à proximité. Openmeta n'est pas un dispositif médical. L'application ne pose pas de diagnostic, ne prescrit pas de traitement et ne remplace ni un professionnel de santé, ni une consultation, ni les services d'urgence. En cas de danger immédiat ou de doute sérieux, l'utilisateur doit appeler le 15 ou le 112.",
        },
      ],
    },
    {
      id: 'personnes',
      title: 'Personnes concernées',
      blocks: [
        {
          type: 'p',
          content:
            "OpenMeta s'adresse à des utilisateurs adultes. Un adulte peut utiliser le service pour lui-même ou pour un proche, notamment un enfant ou une personne âgée qu'il accompagne.",
        },
        {
          type: 'p',
          content:
            "Un mineur ne doit pas créer seul un compte ni utiliser le parcours d'évaluation sans son représentant légal. Si la création autonome par un mineur devait être permise ultérieurement, une information et un consentement adaptés seront mis en place.",
        },
      ],
    },
    {
      id: 'donnees',
      title: 'Données traitées',
      blocks: [
        {
          type: 'p',
          content:
            "OPENMETA traite les catégories de données ci-dessous. Les données effectivement collectées dépendent de la configuration retenue et des prestataires utilisés.",
        },
        { type: 'h3', content: '4.1 Données de compte' },
        {
          type: 'ul',
          content: [
            "Adresse email de connexion.",
            "Prénom ou pseudonyme, à titre facultatif.",
            "Identifiant interne (jamais partagé publiquement).",
            "Mot de passe : conservé uniquement sous forme d'empreinte cryptographique (bcrypt), jamais en clair.",
            "Informations relatives à la création, à la connexion et à la suppression du compte.",
          ],
        },
        { type: 'h3', content: '4.2 Données fournies pendant une évaluation' },
        {
          type: 'ul',
          content: [
            "Âge ou tranche d'âge de la personne concernée par l'évaluation.",
            "Symptômes, réponses aux questions posées, messages libres.",
            "Photos ou documents que vous choisissez volontairement de transmettre.",
            "Messages vocaux si vous utilisez la saisie vocale.",
          ],
        },
        {
          type: 'p',
          content:
            "Ces données peuvent révéler des éléments concernant l'état de santé de la personne concernée. Elles sont donc considérées comme sensibles au sens du RGPD.",
        },
        { type: 'h3', content: '4.3 Localisation' },
        {
          type: 'p',
          content:
            "Lorsque vous utilisez une fonction de recherche de soins à proximité, l'application peut demander l'autorisation d'accéder à la position de l'appareil, conformément aux règles du système d'exploitation. Selon l'implémentation, la localisation est traitée ponctuellement sur l'appareil ou transmise à un prestataire nécessaire à cette fonction. Elle n'est pas conservée au-delà de ce qui est nécessaire au service demandé. Si vous refusez la localisation, une recherche manuelle par ville ou code postal reste disponible.",
        },
        { type: 'h3', content: '4.4 Abonnements et achats' },
        {
          type: 'p',
          content:
            "Pour les abonnements souscrits sur le site openmeta.tech, le paiement est traité par Stripe ; OPENMETA ne reçoit jamais le numéro complet de la carte bancaire.",
        },
        {
          type: 'p',
          content:
            "Si un abonnement ou un achat est proposé via App Store (Apple) ou Google Play, la transaction est réalisée par le magasin d'applications. OPENMETA reçoit uniquement les informations nécessaires au bon fonctionnement du compte : identifiant de transaction, produit acheté, statut, dates de validité. Le numéro complet de carte bancaire n'est jamais transmis à OPENMETA.",
        },
        { type: 'h3', content: '4.5 Données techniques et assistance' },
        {
          type: 'ul',
          content: [
            "Journaux d'erreur, éléments de diagnostic et de sécurité.",
            "Type d'appareil, version de l'application, système d'exploitation.",
            "Échanges avec le support (email, messages).",
            "Identifiants techniques nécessaires au bon fonctionnement des services utilisés (authentification, IA, transcription, achats, hébergement).",
          ],
        },
      ],
    },
    {
      id: 'finalites',
      title: 'Finalités et bases juridiques',
      blocks: [
        {
          type: 'p',
          content:
            "OPENMETA traite les données personnelles pour fournir et améliorer le service, sécuriser les comptes, gérer les abonnements et achats, et répondre à ses obligations légales.",
        },
        {
          type: 'p',
          content:
            "Le consentement aux traitements de données de santé est recueilli par une action positive, spécifique et distincte, avant le démarrage de l'évaluation. Ce consentement peut être retiré à tout moment, sans remettre en cause la licéité des traitements réalisés avant ce retrait. Le retrait empêche l'utilisation des fonctions concernées.",
        },
      ],
    },
    {
      id: 'ia',
      title: 'Traitement par intelligence artificielle',
      blocks: [
        {
          type: 'p',
          content:
            "Lorsque l'utilisateur transmet volontairement du contenu (texte, réponses, voix, photo ou document) dans le cadre d'une fonction assistée par intelligence artificielle, ce contenu peut être transmis à un prestataire technique agissant pour le compte d'OPENMETA.",
        },
        {
          type: 'p',
          content:
            "Avant toute transmission, l'utilisateur reçoit une information claire précisant les catégories de données concernées, le destinataire, la finalité, la durée de conservation et la possibilité de ne pas transmettre. Son autorisation est recueillie avant que la transmission ne soit effectuée.",
        },
        {
          type: 'p',
          content:
            "Les données de santé ne sont ni vendues, ni utilisées à des fins publicitaires ou de profilage. Les conversations ne sont pas conservées de manière persistante (session uniquement), même si des copies techniques temporaires peuvent exister le temps du traitement.",
        },
        {
          type: 'p',
          content:
            "Les paramètres du prestataire d'intelligence artificielle (région d'exécution, durée de conservation, absence d'utilisation à des fins d'entraînement) sont encadrés par les conditions contractuelles du service utilisé.",
        },
      ],
    },
    {
      id: 'destinataires',
      title: 'Destinataires et sous-traitants',
      blocks: [
        {
          type: 'p',
          content:
            "Les données peuvent être communiquées, selon les besoins strictement nécessaires, aux catégories de destinataires suivantes :",
        },
        {
          type: 'ul',
          content: [
            "Personnes habilitées au sein d'OPENMETA (exploitation, support, sécurité).",
            "Hébergeur de l'infrastructure et du site.",
            "Fournisseur d'authentification (gestion des comptes et sessions).",
            "OpenAI ou tout prestataire équivalent, pour les fonctions d'intelligence artificielle.",
            "Fournisseur de transcription (saisie vocale).",
            "Outils de diagnostic et de suivi d'erreurs.",
            "Apple ou Google, pour les achats et abonnements réalisés via App Store ou Google Play.",
            "Prestataires d'assistance et de messagerie.",
            "Autorités légalement habilitées, en cas d'obligation légale ou de procédure.",
          ],
        },
        {
          type: 'p',
          content:
            "Ces prestataires interviennent selon les finalités définies par OPENMETA et sont tenus à des exigences de confidentialité et de sécurité.",
        },
      ],
    },
    {
      id: 'transferts',
      title: 'Transferts hors Espace Économique Européen',
      blocks: [
        {
          type: 'p',
          content:
            "Certains prestataires (notamment ceux liés à l'intelligence artificielle) peuvent traiter tout ou partie des données depuis un pays situé hors de l'Espace Économique Européen. Dans ce cas, OPENMETA met en œuvre les mécanismes reconnus par le RGPD (décision d'adéquation, clauses contractuelles types, avec garanties complémentaires si nécessaire).",
        },
        {
          type: 'p',
          content:
            "Les pays, entités et mécanismes précis peuvent varier selon les prestataires. Les informations correspondantes sont communiquées conformément aux exigences applicables.",
        },
      ],
    },
    {
      id: 'conservation',
      title: 'Durées de conservation',
      blocks: [
        {
          type: 'p',
          content:
            "À l'expiration de l'abonnement, les données sont soit supprimées, soit anonymisées de manière irréversible, sauf obligation légale de conservation ou nécessité pour la défense d'un droit en justice.",
        },
      ],
    },
    {
      id: 'securite',
      title: 'Sécurité',
      blocks: [
        {
          type: 'p',
          content:
            "OPENMETA met en œuvre des mesures techniques et organisationnelles adaptées à la sensibilité des données :",
        },
        {
          type: 'ul',
          content: [
            "Stockage des mots de passe sous forme d'empreinte bcrypt.",
            "Chiffrement en transit des communications.",
            "Selon l'architecture retenue : limitation des accès, authentification des personnes habilitées, journalisation, protection des sauvegardes, gestion des vulnérabilités, chiffrement approprié, procédure de réponse aux incidents.",
          ],
        },
      ],
    },
    {
      id: 'publicite',
      title: 'Absence de publicité fondée sur les données de santé',
      blocks: [
        {
          type: 'p',
          content:
            "OPENMETA ne vend pas de données personnelles. Les données de santé, les évaluations, les photos, les documents et les messages vocaux ne sont jamais utilisés à des fins de publicité ciblée, de courtage de données ou de profilage commercial.",
        },
      ],
    },
    {
      id: 'droits',
      title: 'Droits des utilisateurs',
      blocks: [
        {
          type: 'p',
          content:
            "Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité concernant vos données. Vous pouvez également retirer votre consentement à tout moment.",
        },
        {
          type: 'p',
          content:
            "Vous pouvez, dans le cadre du droit français, définir des directives sur le sort de vos données après votre décès (directives post-mortem).",
        },
        {
          type: 'p',
          content:
            "Toute demande peut être adressée à support@openmeta.fr. En cas de doute raisonnable, une vérification d'identité pourra être demandée.",
        },
        {
          type: 'p',
          content:
            "Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la Commission nationale de l'informatique et des libertés (CNIL) : https://www.cnil.fr/.",
        },
      ],
    },
    {
      id: 'suppression',
      title: 'Suppression du compte',
      blocks: [
        {
          type: 'p',
          content:
            "La suppression du compte peut être initiée directement dans l'application, dans la rubrique « À propos & aide > Supprimer mon compte ». Elle porte sur le compte et les données associées, sauf informations dont la conservation est légalement obligatoire, nécessaire à la sécurité ou à la défense d'un droit en justice. Vous êtes informé, le cas échéant, des données qui doivent être conservées.",
        },
        {
          type: 'p',
          content:
            "Pour Google Play, une page web publique dédiée permet aux utilisateurs de demander la suppression du compte. URL de suppression de compte : https://openmeta.tech.",
        },
        {
          type: 'p',
          content:
            "Pour les comptes créés sur openmeta.tech, la demande de suppression peut être adressée à support@openmeta.fr.",
        },
      ],
    },
    {
      id: 'modifications',
      title: 'Modifications de la politique',
      blocks: [
        {
          type: 'p',
          content:
            "OPENMETA peut mettre à jour cette politique pour refléter l'évolution d'OpenMeta, l'ajout ou le changement de prestataires, ou pour se conformer à de nouvelles obligations légales. La date de dernière mise à jour figure en tête de document. En cas de modification substantielle, une information appropriée est communiquée aux utilisateurs.",
        },
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      blocks: [
        {
          type: 'p',
          content:
            "OPENMETA\n5037 Chemin de la Veillotière\n61330 Ceaucé — France\nTél. +33 2 59 16 64 45\nE-mail : support@openmeta.fr",
        },
      ],
    },
  ],
};
